/**
 * What is out, and with whom — the owner's mandates and consignments, split
 * into their two groups and labelled for the row.
 *
 * Pure. The server's list and the resolved thumbnails go in; a view comes out
 * with every string decided. The copy and the argument for two groups rather
 * than the rail's three are `arrangements.constants.ts`; the single read is
 * `useArrangementsController.ts`. Nothing here changes an arrangement, because
 * ending one belongs on the work's record beside the terms it ends.
 *
 * A consignment's date is in the tense of its state: 'offered' while the
 * gallery has not answered, 'since' the acceptance date once it has. A mandate
 * has one state and so no chip and no figure — see `describeWhen` and
 * `describeTerms`.
 */

import type { Arrangement } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatConsignedDate } from "../gallery/gallery.model.ts";
import { ORG_TYPE_LABELS } from "../profile/profile.constants.ts";
import { ARRANGEMENTS_COPY } from "./arrangements.constants.ts";

/** One arrangement as the row draws it, with every label already decided. */
export interface ArrangementRow {
  arrangementId: string;
  kind: Arrangement["kind"];
  workId: string;
  title: string;
  /** 'John Currin · 2007' — attribution and the code the work carries. */
  identity: string;
  ondoCode: string;
  /** 'Marlow & Finch Art Advisory · Art Advisory · ONDO-1017'. */
  counterparty: string;
  /** 'since 4 Feb 2026' or 'offered 4 Feb 2026'. */
  when: string;
  /** Null on a mandate, which has one state and needs no chip. */
  state: { label: string; tone: "offered" | "accepted" } | null;
  /** 'Consignment · net-of-sale', or "" on a mandate. */
  terms: string;
  href: string;
  imageUrl: string | null;
}

export type ArrangementsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | {
      kind: "ready";
      mandates: ArrangementRow[];
      consignments: ArrangementRow[];
      summary: string;
    };

/**
 * Everything the screen renders, decided here.
 *
 * Two groups off one list, split on `kind` — the shape the SQL returns and the
 * shape the reader's question takes. Within each, the server's order stands:
 * newest first, which for a consignment means the most recent offer at the
 * top, where a reader waiting on a gallery's answer will look for it.
 */
export function toArrangementsView(input: {
  arrangements: Arrangement[] | null;
  error: string | null;
  images: ReadonlyMap<string, string>;
  workBase: string;
}): ArrangementsView {
  const { arrangements, error, images, workBase } = input;

  if (error) return { kind: "error", title: ARRANGEMENTS_COPY.errorTitle, message: error };
  if (!arrangements) return { kind: "loading", message: ARRANGEMENTS_COPY.loading };

  if (arrangements.length === 0) {
    return {
      kind: "empty",
      message: ARRANGEMENTS_COPY.empty,
      detail: ARRANGEMENTS_COPY.emptyDetail,
    };
  }

  const rows = arrangements.map((each) => toRow(each, workBase, images));

  return {
    kind: "ready",
    mandates: rows.filter((row) => row.kind === "mandate"),
    consignments: rows.filter((row) => row.kind === "consignment"),
    summary: ARRANGEMENTS_COPY.summary(rows.length),
  };
}

function toRow(
  each: Arrangement,
  workBase: string,
  images: ReadonlyMap<string, string>,
): ArrangementRow {
  return {
    arrangementId: each.arrangementId,
    kind: each.kind,
    workId: each.workId,
    title: each.title,
    identity: each.attributedName,
    ondoCode: each.ondoCode,
    counterparty: describeCounterparty(each),
    when: describeWhen(each),
    state: describeState(each),
    terms: describeTerms(each),
    href: `${workBase}/${each.workId}`,
    imageUrl: images.get(each.workId) ?? null,
  };
}

/**
 * 'Marlow & Finch Art Advisory · Art Advisory · ONDO-1017'.
 *
 * The kind of organisation is named in the middle, because it is the fact that
 * distinguishes the two groups' counterparties at a glance — and a person, who
 * has no kind, simply has the middle term absent rather than "Person".
 */
export function describeCounterparty(each: Arrangement): string {
  const kind = each.counterpartyOrgType ? ORG_TYPE_LABELS[each.counterpartyOrgType] : null;

  return [each.counterpartyName, kind, each.counterpartyOndoId]
    .filter((part) => part !== null)
    .join(" · ");
}

/**
 * When it began, in the tense the row is in.
 *
 * A mandate is 'since': it is a standing arrangement with one state. An
 * unanswered consignment is 'offered', because the date is the date of an ask
 * that has not been answered — and once it has been, it is 'since' again, on
 * the date the gallery took it.
 */
export function describeWhen(each: Arrangement): string {
  if (each.kind === "consignment" && each.acceptedAt === null) {
    return `${ARRANGEMENTS_COPY.offeredOn} ${formatConsignedDate(each.beganAt)}`;
  }

  const from = each.kind === "consignment" && each.acceptedAt ? each.acceptedAt : each.beganAt;

  return `${ARRANGEMENTS_COPY.since} ${formatConsignedDate(from)}`;
}

function describeState(each: Arrangement): ArrangementRow["state"] {
  if (each.kind !== "consignment") return null;

  return each.acceptedAt === null
    ? { label: ARRANGEMENTS_COPY.state.offered, tone: "offered" }
    : { label: ARRANGEMENTS_COPY.state.accepted, tone: "accepted" };
}

/** 'Consignment · net-of-sale', or "" on a mandate. No figure — decision 5. */
export function describeTerms(each: Arrangement): string {
  if (each.kind !== "consignment" || !each.arrangement) return "";

  const basis = each.basis ? ARRANGEMENTS_COPY.basis[each.basis] : "";

  return [ARRANGEMENTS_COPY.arrangement[each.arrangement], basis]
    .filter((part) => part !== "")
    .join(" · ");
}

export function describeArrangementsError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.message) return cause.message;
  }

  return ARRANGEMENTS_COPY.errorTitle;
}
